import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { LocalIssue, LocalIssueCategory, LocalIssueStatus, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';
import { IssueDetail } from './IssueDetail';

const STATUS_OPTIONS: LocalIssueStatus[] = ['REPORTED', 'ACKNOWLEDGED', 'REFERRED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED', 'REJECTED'];

const CATEGORY_OPTIONS: LocalIssueCategory[] = [
  'ROAD_DEFECT',
  'DRAINAGE',
  'STREETLIGHT',
  'WASTE_SANITATION',
  'NOISE_NUISANCE',
  'MARKET_ISSUE',
  'PUBLIC_SAFETY',
  'PARKS_GROUNDS',
  'OTHER',
];

// Mirrors M41_REFERRAL_CATEGORIES in service/constants.ts — used only to
// show the routeTarget a category WOULD get before logging a walk-in
// report; the API derives and stores this itself at create time.
const M20_REFERRAL_CATEGORIES: readonly LocalIssueCategory[] = ['ROAD_DEFECT', 'DRAINAGE', 'STREETLIGHT', 'PARKS_GROUNDS'];

/**
 * Local-issue triage — GET /issues?district=<own district> (staff-only
 * endpoint) with status/category filters. Every category is fixed at
 * report time to a routeTarget (M20_REFERRAL vs DISTRICT_LEVEL — see
 * service/constants.ts#M20_REFERRAL_CATEGORIES), shown as its own badge
 * next to the status so the triage decision is always visible in the list,
 * not just inside the detail view.
 */
export function IssuesConsole() {
  const { persona } = useAuth();
  const district = persona?.district ?? '';
  const [status, setStatus] = useState<LocalIssueStatus | ''>('');
  const [category, setCategory] = useState<LocalIssueCategory | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showLog, setShowLog] = useState(false);

  const qs = new URLSearchParams({ limit: '100' });
  if (district) qs.set('district', district);
  if (status) qs.set('status', status);
  if (category) qs.set('category', category);
  const issuesQ = useApiQuery<Page<LocalIssue>>(district ? `/api/v1/m41/issues?${qs.toString()}` : null);
  const issues = issuesQ.data?.items ?? [];

  if (!district) return <ErrorNote message="This account has no district binding on file." />;

  return (
    <div>
      <PageHeader
        title="Local Issues"
        subtitle="Triage citizen reports — route to M20 Infrastructure or resolve at district level."
        actions={
          <button className="btn-secondary" onClick={() => setShowLog(true)}>
            + Log walk-in report
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-3">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as LocalIssueStatus | '')}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
        <select className="field-input !w-auto" value={category} onChange={(e) => setCategory(e.target.value as LocalIssueCategory | '')}>
          <option value="">All categories</option>
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {c.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {issuesQ.error ? (
        <div className="mb-4">
          <ErrorNote message={issuesQ.error} />
        </div>
      ) : null}

      <div className="card overflow-x-auto">
        {issuesQ.loading ? (
          <Spinner />
        ) : issues.length === 0 ? (
          <EmptyState title="No issues match these filters" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Category</th>
                <th className="table-head">Routes to</th>
                <th className="table-head">Status</th>
                <th className="table-head">Reported</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i) => (
                <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(i.id)}>
                  <td className="table-cell font-mono text-xs">{i.refNo}</td>
                  <td className="table-cell">{i.category.replaceAll('_', ' ')}</td>
                  <td className="table-cell">
                    <StatusBadge status={i.routeTarget} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={i.status} />
                  </td>
                  <td className="table-cell text-xs text-slate-500">{formatDateTime(i.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedId ? (
        <IssueDetail id={selectedId} onClose={() => setSelectedId(null)} onChanged={() => issuesQ.reload()} />
      ) : null}
      {showLog ? (
        <LogIssueModal
          district={district}
          onClose={() => setShowLog(false)}
          onDone={() => {
            setShowLog(false);
            issuesQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function LogIssueModal({ district, onClose, onDone }: { district: string; onClose: () => void; onDone: () => void }) {
  const [category, setCategory] = useState<LocalIssueCategory>('OTHER');
  const [description, setDescription] = useState('');
  const [locationDescription, setLocationDescription] = useState('');
  const [reporterContact, setReporterContact] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m41/issues', {
        district,
        category,
        description: description.trim(),
        locationDescription: locationDescription.trim() || undefined,
        reporterContact: reporterContact.trim() || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to log issue');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log a walk-in issue report" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          M41 attributes the report to whoever calls the API — there is no "on behalf of a resident" field, so this
          logs the report under your own staff identity for a resident who came to the office in person.
        </p>
        <Field label="Category">
          <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as LocalIssueCategory)}>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c} value={c}>
                {c.replaceAll('_', ' ')} — routes to {M20_REFERRAL_CATEGORIES.includes(c) ? 'M20 Infrastructure' : 'district level'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <Field label="Location description (optional)">
          <input className="field-input" value={locationDescription} onChange={(e) => setLocationDescription(e.target.value)} placeholder="e.g. Coast road near the community hall junction" />
        </Field>
        <Field label="Reporter contact (optional)">
          <input className="field-input" value={reporterContact} onChange={(e) => setReporterContact(e.target.value)} placeholder="phone or email of the resident" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || description.trim().length < 2}>
            {busy ? 'Logging…' : 'Log report'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
